/**
 * The players' path through the site (author's addition), counted by the server for the admin page (Analytics.cs):
 * a visit, the match page, a room, a match started, a match finished. Each step is sent once per tab session (a
 * reload or a second match does not count again: the admin page reads how many sessions get how far), with its door:
 * the site or Discord, a phone or a computer, and for the visit the page and the site that sent the visitor.
 *
 * Nothing personal: no cookie, no identifier, only these words. Sent with sendBeacon (it leaves even when the page
 * closes, and asks for no answer); under Discord's proxy the address is relative, so it goes through it like the rest.
 */
import { SERVER_URL } from '../net/config';
import { discordInstance } from './discord';

export type Step = 'visit' | 'play' | 'room' | 'watch' | 'start' | 'finish';

/** The server's address, from the relay's (ws://host/ws → http://host/api/event): under Vite too, the server is elsewhere. */
const EVENT_URL = SERVER_URL.replace(/^ws/, 'http').replace(/\/ws$/, '/api/event');
const KEY = 'invaders.funnel';

/** The steps this tab session already sent (sessionStorage; in memory if the browser refuses it). */
let sent: Set<string> | null = null;
function sentSteps(): Set<string> {
  if (sent) return sent;
  try { sent = new Set(JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as string[]); } catch { sent = new Set(); }
  return sent;
}

/** Where the visitor came from: a `utm_source` or `ref` in the address (a link posted somewhere), else the referring site. */
function origin(): string | undefined {
  const params = new URLSearchParams(location.search);
  const tagged = params.get('utm_source') ?? params.get('ref');
  if (tagged) return tagged.toLowerCase().replace(/[^a-z0-9.-]/g, '').slice(0, 64) || undefined;
  try {
    const host = document.referrer ? new URL(document.referrer).hostname : '';
    return host && host !== location.hostname ? host : undefined;
  } catch { return undefined; }
}

export function track(step: Step, extra: { page?: 'title' | 'match' | 'gameplay'; mode?: 'online' | 'local' } = {}): void {
  const steps = sentSteps();
  if (steps.has(step)) return;
  steps.add(step);
  try { sessionStorage.setItem(KEY, JSON.stringify([...steps])); } catch { /* counted in memory only */ }
  const body = JSON.stringify({
    step,
    src: discordInstance ? 'discord' : 'site',
    device: matchMedia('(pointer: coarse)').matches ? 'mobile' : 'desktop',
    ...extra,
    ...(step === 'visit' ? { ref: origin() } : {}),
  });
  try {
    if (!navigator.sendBeacon?.(EVENT_URL, body)) void fetch(EVENT_URL, { method: 'POST', body, keepalive: true }).catch(() => undefined);
  } catch { /* the count is never worth an error */ }
}
